// hooks/useImageCheck.ts
import { useState, useCallback } from 'react';
import { message, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import React from 'react';
export type ImageType = 'BASE64' | 'URL';

export interface ImageInfo {
    name: string;
    size: number;
    lastModified: string;
    type: string;
    dimensions?: { width: number; height: number };
}

export interface UseImageCheckOptions {
    // 必传参数
    request: (params: any) => Promise<any>;

    // 可选参数
    onSuccess?: () => void;
    successMessage?: string;
    maxSize?: number; // MB，默认2MB
    imageType?: ImageType; // 支持的类型，默认两种都支持
    createConfirmContent?: (params: any) => React.ReactNode;
    needName?: boolean;
    needIdNo?: boolean;
}

export const useImageCheck = (options: UseImageCheckOptions) => {
    const [fileList, setFileList] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState<string>('');
    const [imageInfo, setImageInfo] = useState<ImageInfo | null>(null);
    const [imageValid, setImageValid] = useState(false);

    const {
        request,
        onSuccess,
        successMessage = '提交成功',
        maxSize = 2,
        imageType = 'BASE64',
        createConfirmContent,
        needName = false,
        needIdNo = false,
    } = options;

    // 验证图片文件
    const validateImageFile = useCallback((file: File) => {
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/bmp', 'image/gif'];
        const isValidType = validTypes.includes(file.type);

        if (!isValidType) {
            message.error('只能上传图片文件（支持 JPG、PNG、BMP、GIF 格式）');
            return false;
        }

        // 验证文件大小
        const isLtMax = file.size / 1024 / 1024 < maxSize;
        if (!isLtMax) {
            message.error(`图片大小不能超过${maxSize}MB`);
            return false;
        }

        if (file.name.length > 100) {
            message.error('文件名过长，请重命名后上传');
            return false;
        }

        return true;
    }, [maxSize]);

    // 获取图片尺寸
    const getImageDimensions = useCallback((file: File): Promise<{ width: number; height: number }> => {
        return new Promise((resolve) => {
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => {
                resolve({ width: img.width, height: img.height });
                URL.revokeObjectURL(url);
            };
            img.src = url;
        });
    }, []);

    // 处理图片上传
    const handleImageUpload = useCallback(async (file: File) => {
        setImageValid(false);

        if (!validateImageFile(file)) {
            setFileList([]);
            setImagePreview('');
            setImageInfo(null);
            return false;
        }

        try {
            const dimensions = await getImageDimensions(file);

            setImageInfo({
                name: file.name,
                size: file.size,
                lastModified: new Date(file.lastModified).toLocaleString(),
                type: file.type,
                dimensions,
            });

            // 生成预览图
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);

            setFileList([file]);
            setImageValid(true);
            message.success('图片上传成功');
            return true;
        } catch (error) {
            console.error('图片上传失败:', error);
            return false;
        }
    }, [validateImageFile, getImageDimensions]);

    // 自定义上传请求
    const customRequest = useCallback(({ file, onSuccess: onUploadSuccess }: any) => {
        if (file instanceof File) {
            handleImageUpload(file);
        }
        onUploadSuccess?.('ok');
    }, [handleImageUpload]);

    // 移除图片
    const removeImage = useCallback(() => {
        setFileList([]);
        setImagePreview('');
        setImageInfo(null);
        setImageValid(false);
    }, []);

    // 获取文件大小格式
    const formatFileSize = useCallback((bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    // 开始验证（通用方法）
    const startCheck = useCallback(async (params: {
        form: any;
        imageType: ImageType;
        imageUrl?: string;
        customConfirmContent?: (values: any) => React.ReactNode;
    }) => {
        const { form, imageType, imageUrl, customConfirmContent } = params;

        try {
            setLoading(true);

            // 验证必填字段
            const fields = [];
            if (needName) fields.push('name');
            if (needIdNo) fields.push('idNo');
            if (imageType === 'URL') fields.push('imageUrl');

            const values = await form.validateFields(fields);

            // 验证图片数据
            if (imageType === 'BASE64' && !fileList.length) {
                message.warning('请上传图片');
                setLoading(false);
                return;
            }

            // 准备确认对话框内容
            const confirmContent = customConfirmContent
                ? customConfirmContent({ values, imageInfo, fileList: fileList[0], imageType, formatFileSize })
                : createConfirmContent
                    ? createConfirmContent({ values, imageInfo, fileList: fileList[0], imageType, formatFileSize })
                    : null;

            Modal.confirm({
                title: '确认要开始验证吗？',
                icon: <ExclamationCircleOutlined />,
                content: confirmContent || (
                    <div>
                        <div>即将验证的信息：</div>
                        <div style={{ marginTop: 8, color: '#faad14' }}>
                            启动后将不支持取消，请确认！
                        </div>
                    </div>
                ),
                okButtonProps: {
                    loading,
                        disabled: loading,
                },
                onOk: async () => {
                    try {
                        // 构建请求参数
                        const requestParams: any = { ...values,imageType,imageUrl };

                        if (imageType === 'BASE64' && fileList.length) {
                            const file = fileList[0];
                            const reader = new FileReader();
                            const base64String = await new Promise<string>((resolve) => {
                                reader.onload = (e) => {
                                    const base64 = e.target?.result as string;
                                    const base64WithoutHeader = base64.replace(/^data:image\/\w+;base64,/, '');
                                    resolve(base64WithoutHeader);
                                };
                                reader.readAsDataURL(file);
                            });
                            requestParams.image = base64String;
                        } else if (imageType === 'URL') {
                            requestParams.image = imageUrl;
                        }

                        await request(requestParams);
                        message.success(successMessage);

                        // 重置表单
                        setTimeout(() => {
                            removeImage();
                            if (form) form.resetFields();
                            onSuccess?.();
                        }, 1000);
                    } catch (error: any) {
                        console.error('提交失败:', error);
                    } finally {
                        setLoading(false);
                    }
                },
                    onCancel: () => {
                    setLoading(false);
                },
            });
        } catch (error) {
            console.error('表单验证失败:', error);
            setLoading(false);
        }
    }, [fileList, request, successMessage, removeImage, onSuccess, needName, needIdNo, createConfirmContent, formatFileSize, imageInfo]);

    return {
        // 状态
        fileList,
        loading,
        imagePreview,
        imageInfo,
        imageValid,

        // 方法
        customRequest,
        startCheck,
        removeImage,
        formatFileSize,
        handleImageUpload,

        // 计算属性
        finalDisabled: fileList.length > 0,
    };
};

// 默认的确认对话框内容创建函数
export const createDefaultConfirmContent = (params: {
    values: any;
    imageInfo?: ImageInfo | null;
    fileList?: any;
    imageType: ImageType;
    formatFileSize: (bytes: number) => string;
}) => {
    const { values, imageInfo, fileList, imageType, formatFileSize } = params;

    return (
        <div>
            <div>即将验证的信息：</div>

    {values.name && (
        <div style={{ marginTop: 8 }}>
        <strong>姓名：</strong>{values.name}
    </div>
    )}

    {values.idNo && (
        <div style={{ marginTop: 4 }}>
        <strong>身份证号：</strong>{values.idNo}
    </div>
    )}

    <div style={{ marginTop: 8 }}>
    <strong>图片类型：</strong>
    {imageType === 'URL' ? '图片路径' : '上传图片'}
    </div>

    {imageType === 'URL' && values.imageUrl && (
        <div style={{ marginTop: 4 }}>
            <span style={{ fontSize: 12, color: '#666', wordBreak: 'break-all' }}>
            {values.imageUrl}
        </span>
        </div>
    )}

    {imageType === 'BASE64' && imageInfo && (
        <div style={{ marginTop: 8 }}>
        <strong>图片文件：</strong>{imageInfo.name}
    <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
        文件大小：{formatFileSize(imageInfo.size)}
        </div>
        </div>
    )}

    <div style={{ marginTop: 8, color: '#faad14' }}>
    启动后将不支持取消，请确认！
      </div>
      </div>
);
};